import { FC, forwardRef, useEffect, useMemo, useState } from 'react';
import { useController, useFormContext } from 'react-hook-form';
import PhoneInput, {
  Country,
  getCountryCallingCode,
  isValidPhoneNumber,
  parsePhoneNumber,
} from 'react-phone-number-input';
import 'react-phone-number-input/style.css';

import { Button, FormControl, Stack, Tooltip } from '@mui/material';

import { ReactComponent as FormInfo } from '@/assets/icons/form-info-icon.svg';
import { camelToSnakeCase } from '@/shared/lib/strings';
import { FormError, FormLabel } from '@/shared/ui/styled';

import { PhoneInputContainer } from './RhfPhoneInput.styles';

type Props = {
  name: string;
  label?: string;
  tooltip?: string;
  placeholder?: string;
  isRequired?: boolean;
  disabled?: boolean;
};

const InputWithoutZero = forwardRef<HTMLInputElement, any>((props, ref) => (
  <input
    {...props}
    ref={ref}
    value={props.value?.startsWith('0') ? props.value.substring(1) : props.value}
  />
));

InputWithoutZero.displayName = 'InputWithoutZero';

const normalizePhoneValue = (raw: string | null | undefined, country: Country): string => {
  if (!raw) return '';
  const cleaned = raw.replace(/[^\d+]/g, '').replace(/^00/, '+');
  if (cleaned.startsWith('+')) return cleaned;
  return parsePhoneNumber(cleaned, country)?.number ?? cleaned;
};

const RhfPhoneInput: FC<Props> = ({ name, label, tooltip, disabled }) => {
  const [touched, setTouched] = useState<boolean>(false);
  const [phoneNumber, setPhoneNumber] = useState<string>('');
  const [country, setCountry] = useState<Country>('RO');

  const { setValue } = useFormContext();
  const {
    field: { value, onChange },
    fieldState: { error },
    formState: { isSubmitting },
  } = useController({ name });

  const normalizedValue = useMemo(() => normalizePhoneValue(value, country), [value, country]);

  useEffect(() => {
    if (value && normalizedValue !== value) {
      setValue(name, normalizedValue);
    }
  }, [name, value, normalizedValue, setValue]);

  const isValid = useMemo(
    () => !touched || !phoneNumber || isValidPhoneNumber(phoneNumber),
    [touched, phoneNumber],
  );

  const callingCode = useMemo(() => {
    try {
      return country ? getCountryCallingCode(country) : '';
    } catch (e) {
      return '';
    }
  }, [country]);

  return (
    <FormControl fullWidth error={!!error}>
      {!!label && (
        <Stack direction="row" alignItems="center" justifyContent="space-between">
          <FormLabel
            sx={{
              fontWeight: 500,
              lineHeight: '120%',
              fontSize: '13px',
              color: '#333239',
              fontFamily: '"Neue Frutiger World"',
              letterSpacing: '0.26px',
            }}
          >
            {label}
          </FormLabel>
          {!!tooltip && (
            <Tooltip title={tooltip}>
              <span>
                <Button type="button" sx={{ padding: '0.25rem' }} disableRipple>
                  <FormInfo />
                </Button>
              </span>
            </Tooltip>
          )}
        </Stack>
      )}

      <PhoneInputContainer
        callingCode={callingCode}
        hasError={!!error}
        isValid={isValid}
        isDisabled={disabled}
      >
        <PhoneInput
          placeholder={'736352251'}
          value={normalizedValue}
          international={false}
          id={`${camelToSnakeCase(name)}-input`}
          inputComponent={InputWithoutZero}
          onChange={(value) => {
            setTouched(true);
            setPhoneNumber(value?.toString() || '');
            onChange(value?.toString() || '');
          }}
          onCountryChange={(country) => setCountry(country ?? 'RO')}
          autoComplete="new-password"
          defaultCountry={country}
          disabled={disabled || isSubmitting}
        />
      </PhoneInputContainer>

      {(!!error || !isValid) && (
        <FormError>{error ? error?.message || '' : 'Invalid phone number'}</FormError>
      )}
    </FormControl>
  );
};

export default RhfPhoneInput;
